import React, { useEffect, useState, useRef, memo } from "react";
import {
  Box,
  Text,
  VStack,
  Radio,
  RadioGroup,
  Switch,
  Button,
  Input,
  useColorModeValue,
  useColorMode,
  useToast,
  Flex,
  Tooltip,
  Image,
  CheckboxGroup,
  Checkbox,
  HStack
} from "@chakra-ui/react";
import Select from "react-select";
import { cqAPI } from "../../lib/Auth";
import _ from "lodash";
import dialogManager from "../DialogManager/DialogManager";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Faux switch for print mode (renders reliably with html2canvas)
const FauxSwitch = ({
  checked,
  labelOn = "Yes",
  labelOff = "No",
  colorScheme = "blue", // match Chakra's default color scheme
}) => (
  <Flex
    align="center"
    gap={3}
    mt={2}
    mb={1}
  >
    <Box
      as="span"
      display="inline-block"
      position="relative"
      width="44px"
      height="24px"
      verticalAlign="middle"
    >
      {/* Track */}
      <Box
        position="absolute"
        inset="0"
        borderRadius="9999px"
        bg={checked ? `${colorScheme}.400` : "gray.300"}
        border="1px solid"
        borderColor={checked ? `${colorScheme}.500` : "gray.400"}
      />
      {/* Knob */}
      <Box
        position="absolute"
        top="50%"
        left="3px"
        transform={checked ? "translate(20px, -50%)" : "translate(0, -50%)"}
        transition="transform 0.1s linear"
        width="18px"
        height="18px"
        borderRadius="50%"
        bg="white"
        border="1px solid"
        borderColor="gray.300"
        boxShadow="sm"
      />
    </Box>
    <Text mt="-1px">{checked ? labelOn : labelOff}</Text>
  </Flex>
);



// Memoized Input Component
const MemoizedInput = memo(({ id, value, onChange, placeholder, isReadOnly }) => {
  return (
    <Input
      id={id}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      isReadOnly={isReadOnly}
    />
  );
});

const GenericSettings = ({
  configuration,
  endpoint,
  onClose,
  method = "post",
  viewOnly = false,
  supportBatching = false,
  autoSave = false,
  bid_uid = "",
  long_query = false,
  description = undefined,
  saveButton,
  // Added (safe): lets PatientHistory know when data is ready
  onLoad,
  // Added (safe): render PDF-friendly static UI
  printMode = false,
  toolbarText = ""
}) => {
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const invalidInputRef = useRef(null);
  const toast = useToast();
  const { colorMode } = useColorMode();
  const tmp_method = long_query ? "post" : "get";

  const fetchSettings = async () => {
    if (!endpoint) return;

    const results = supportBatching
      ? tmp_method === "get"
        ? await cqAPI
            .get(
              `${endpoint}?batch=true${bid_uid}&id=${configuration
                .map((section) => section.inputs.map((input) => input.id).join())
                .join()}`
            )
            .then((res) => res.data || [])
        : await cqAPI
            .post(`${endpoint}?batch=true${bid_uid}`, {
              id: configuration
                .map((section) => section.inputs.map((input) => input.id).join())
                .join(),
            })
            .then((res) => res.data || [])
      : await Promise.all(
          configuration
            .map((section) =>
              section.inputs.map((input) =>
                cqAPI.get(`${endpoint}?id=${input.id}`).then((res) => ({
                  id: input.id,
                  value: res.data,
                }))
              )
            )
            .flat()
        );

    const settingsData = Array.isArray(results)
      ? results.reduce((acc, { id, value }) => {
          acc[id] = value;
          return acc;
        }, {})
      : results;

    setSettings(settingsData);
    setOriginalSettings(_.cloneDeep(settingsData));
  };

  useEffect(() => {
    fetchSettings();
  }, [endpoint, configuration]);

  // Fire onLoad once data has arrived (harmless unless used)
  useEffect(() => {
    if (onLoad && Object.keys(settings).length > 0) onLoad();
  }, [settings, onLoad]);

  const handleRadioChange = (id, value) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [id]: value,
    }));
  };

  const handleCheckboxChange = (id, value, checked) => {
    setSettings((prev) => {
      const current = Array.isArray(prev[id]) ? prev[id] : [];
      const nextVals = checked
        ? current.includes(value)
          ? current
          : [...current, value]
        : current.filter((v) => v !== value);
      return { ...prev, [id]: nextVals };
    });
  };

  const handleSwitchChange = (id, event) => {
    const isChecked = event.target.checked;
    setSettings((prevSettings) => ({
      ...prevSettings,
      [id]: !!isChecked,
    }));
  };

  const handleInputChange = (id, event) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [id]: event.target.value,
    }));
  };

  const handleSelectChange = (id, selectedOptions, isMulti) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [id]: isMulti
        ? selectedOptions
          ? selectedOptions.map((option) => option.value)
          : []
        : selectedOptions.value,
    }));
  };


  const inputContainersRef = useRef({});

  const isEmptyValue = (input, value) => {
    switch (input.type) {
      case "input":
      case "text":
        return value == null || String(value).trim() === "";
      case "radio":
        return value == null || value === "";
      case "checkbox":
        return !Array.isArray(value) || value.length === 0;
      case "select":
        return input.isMulti
          ? !Array.isArray(value) || value.length === 0
          : value == null || value === "";
      // should not enforce value for switch
      case "switch":
      case "switchwt":
        return false
        // return value === undefined || value === null || value === "";
      default:
        return value == null || value === "";
    }
  };

  const validateRequiredFields = () => {
    for (const section of configuration) {
      for (const input of section.inputs) {
        if (input.required) {
          const val = settings[input.id];
          if (isEmptyValue(input, val)) {
            invalidInputRef.current = input.id;

            toast({
              title: "Missing required field",
              description: `${input.name || input.id} is required.`,
              status: "error",
              duration: 3000,
              isClosable: true,
            });

            const native = document.getElementById(input.id);
            if (native && typeof native.focus === "function") {
              native.focus();
            } else {
              const wrapper = inputContainersRef.current[input.id];
              if (wrapper && typeof wrapper.scrollIntoView === "function") {
                wrapper.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }

            return false;
          }
        }
      }
    }
    return true;
  };


  const saveChanges = async (fullydone) => {
    // Validate any regex patterns declared on inputs
    for (const section of configuration) {
      for (const input of section.inputs) {
        if (input.type === "input" && input.pattern) {
          const regex = new RegExp(input.pattern);
          if (!regex.test(settings[input.id] || "")) {
            invalidInputRef.current = input.id;
            toast({
              title: "Invalid input",
              description: `${input.name || input.id} is invalid. Please check your input.`,
              status: "error",
              duration: 3000,
              isClosable: true,
            });
            const elem = document.getElementById(input.id);
            if (elem) elem.focus();
            return;
          }
        }
      }
    }

    // Compute changes
    const changes = {};
    Object.keys(settings).forEach((key) => {
      if (!_.isEqual(settings[key], originalSettings[key])) {
        changes[key] = settings[key];
      }
    });

    const results = supportBatching
      ? await cqAPI[method](
          `${endpoint}?batch=true`,
          Object.keys(changes).map((key) => ({ key, value: changes[key] }))
        )
      : await Promise.all(
          Object.keys(changes).map((key) =>
            endpoint
              ? cqAPI[method](`${endpoint}`, { key: key, value: changes[key] })
              : Promise.resolve({ type: "ok" })
          )
        );

    const errors = supportBatching ? [results].filter((res) => res.msg) : results.filter((res) => res.msg);

    if (errors.length > 0) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: errors.map((error) => error.msg).join("\n"),
        header: "Error",
      });
    } else {
      if (!autoSave) {
        fetchSettings();
      }
    }
    if (fullydone){
      if (!validateRequiredFields()) return
      onClose && onClose(true);
    }
  };

  useEffect(() => {
    const changesDetected = !_.isEqual(settings, originalSettings);
    if (autoSave && changesDetected) {
      saveChanges();
    }
    setHasChanges(changesDetected);
  }, [settings, originalSettings]);


  const bgColor = useColorModeValue("gray.100", "gray.700");
  const hintColor = useColorModeValue("gray.500", "gray.400");

  // Display-friendly value formatter for print mode
  const renderValue = (input) => {
    const val = settings[input.id];
    if (val == null) return "—";
    const opts = input.options || [];

    switch (input.type) {
      case "switch":
        return val ? "Yes" : "No";
      case "switchwt":
        return typeof val === "string" ? val : val ? "Yes" : "No";
      case "radio":
        return opts.find((o) => o.value === val)?.label ?? String(val);
      case "checkbox": {
        const arr = Array.isArray(val) ? val : [];
        const labels = arr.map((v) => opts.find((o) => o.value === v)?.label ?? v);
        return labels.length ? labels.join(", ") : "—";
      }
      case "select":
        if (input.isMulti) {
          const arr = Array.isArray(val) ? val : [];
          const labels = arr.map((v) => opts.find((o) => o.value === v)?.label ?? v);
          return labels.length ? labels.join(", ") : "—";
        }
        return opts.find((o) => o.value === val)?.label ?? String(val);
      case "input":
      case "text":
      default:
        return typeof val === "object" ? JSON.stringify(val) : String(val);
    }
  };
  
  return (
    <Box p={4} w="100%" align="left">
      {/* Toolbar: hide in printMode, otherwise:
          - Always show Close if provided
          - Show Save when (!autoSave || saveButton) and not viewOnly
      */}
      {!printMode && (onClose || (!viewOnly && (!autoSave || saveButton))) && (
        <HStack
          justify={"flex-end"}
          textAlign="right"
          mt={4}
          p={4}
          position="sticky"
          top="0"
          zIndex={"200"}
          borderRadius="md"
          gap={3}
        >
          {toolbarText && (
            <Text width="100%" whiteSpace="noWrap" isTruncated fontSize='lg' as='b'>{toolbarText}</Text>
          )}
          {!viewOnly && (!autoSave || saveButton) && endpoint && (
            <Button colorScheme="blue" isDisabled={!hasChanges} onClick={autoSave? ()=>saveChanges(true) : ()=>saveChanges() }>
              Save
            </Button>
          )}
          {onClose && (
            <Button onClick={() => {
              if (!validateRequiredFields()) return;
              onClose && onClose();
            }}>
              Close
            </Button>
          )}
        </HStack>
      )}
      {printMode && toolbarText && (
        <Flex
          ustify={"flex-end"}
          textAlign="right"
          mt={4}
          p={4}
          position="sticky"
          top="0"
          zIndex={"200"}
          borderRadius="md"
          gap={3}
        >
          <Text width="100%" whiteSpace="noWrap" isTruncated fontSize='xl' as='b'>{toolbarText}</Text>
        </Flex>
      )}

      {description && (
        <Box key={description} mb={4} p={4} bgColor={bgColor} borderRadius="md">
          <Text>{description}</Text>
        </Box>
      )}

      {configuration.map((section, index) => (
        <Box key={index} mb={4} p={4} bgColor={bgColor} borderRadius="md">
          <Text mb={4}>{section.title}</Text>
          <Text mb={4}>{section.description}</Text>
          {section.inputs.map((input, idx) => (
            <Box key={idx} mb={4}>
              {!(input.icon || input.custom_icon) && <Text mb={1}>{input.name} {input.required && (
                <Text as="span" color="red.500" ml={1}>
                  *
                </Text>
              )}</Text>}
              {input.hint && (
                <Text fontSize="sm" mb={2} color={hintColor}>
                  {input.hint}
                </Text>
              )}

              {/* Radio */}
              {input.type === "radio" &&
                (printMode ? (
                  <Text>{renderValue(input)}</Text>
                ) : (
                  <RadioGroup
                    onChange={(value) => handleRadioChange(input.id, value)}
                    value={settings[input.id] || ""}
                    isDisabled={viewOnly}
                  >
                    <VStack align="start">
                      {input.options.map((option) => (
                        <Radio key={option.value} value={option.value}>
                          {option.label}
                        </Radio>
                      ))}
                    </VStack>
                  </RadioGroup>
                ))}

              {/* Checkbox */}
              {input.type === "checkbox" &&
                (printMode ? (
                  <Text>{renderValue(input)}</Text>
                ) : (
                  <CheckboxGroup
                    value={Array.isArray(settings[input.id]) ? settings[input.id] : []}
                    isDisabled={viewOnly}
                  >
                    <VStack align={"start"}>
                      {input.options.map((option) => (
                        <Checkbox
                          key={option.value}
                          value={option.value}
                          onChange={(e) =>
                            handleCheckboxChange(input.id, option.value, e.target.checked)
                          }
                          defaultChecked={
                            settings[input.id] ? settings[input.id].includes(option.value) : false
                          }
                        >
                          {option.label}
                        </Checkbox>
                      ))}
                    </VStack>
                  </CheckboxGroup>
                ))}

              {/* Switch */}
              {input.type === "switch" &&
                (printMode ? (
                  <FauxSwitch checked={!!settings[input.id]} labelOn="Yes" labelOff="No" colorScheme="blue" />
                ) : (
                  <Switch
                    isChecked={settings[input.id]}
                    onChange={(event) => handleSwitchChange(input.id, event)}
                    isDisabled={viewOnly}
                  />
                ))}

              {/* Switch with text */}
              {input.type === "switchwt" &&
                (printMode ? (
                  <Flex direction="column" gap={2}>
                    <FauxSwitch checked={!!settings[input.id]} labelOn="Yes" labelOff="No" colorScheme="blue" />
                    {typeof settings[input.id] === "string" &&
                      settings[input.id].trim() !== "" && <Text>{settings[input.id]}</Text>}
                  </Flex>
                ) : (
                  <>
                    <Switch
                      isChecked={settings[input.id]}
                      onChange={(event) => handleSwitchChange(input.id, event)}
                      isDisabled={viewOnly}
                    />
                    {settings[input.id] !== undefined && (
                      <MemoizedInput
                        id={input.id}
                        value={
                          typeof settings[input.id] === "string" ? settings[input.id] : ""
                        }
                        onChange={(event) => handleInputChange(input.id, event)}
                        placeholder={`Enter ${input.name} details`}
                        isReadOnly={viewOnly}
                      />
                    )}
                  </>
                ))}

              {/* Input */}
              {input.type === "input" &&
                (printMode ? (
                  <Text>{renderValue(input)}</Text>
                ) : (
                  <Flex align="center">
                    {input.icon && (
                      <Tooltip label={input.name}>
                        <FontAwesomeIcon
                          icon={input.icon}
                          style={{ marginRight: "8px" }}
                          size="lg"
                        />
                      </Tooltip>
                    )}
                    {input.custom_icon && (
                      <Tooltip label={input.name}>
                        <Image src={input.custom_icon} alt={input.name} boxSize="16px" mr="8px" />
                      </Tooltip>
                    )}
                    <MemoizedInput
                      id={input.id}
                      value={settings[input.id]}
                      onChange={(event) => handleInputChange(input.id, event)}
                      placeholder={input.name}
                      isReadOnly={viewOnly}
                    />
                  </Flex>
                ))}

              {/* Select */}
              {input.type === "select" &&
                (printMode ? (
                  <Text>{renderValue(input)}</Text>
                ) : (
                  <Select
                    styles={{
                      control: (base) => ({
                        ...base,
                        background: colorMode === "light" ? "#F7FAFC" : "#2D3748",
                        borderColor: colorMode === "light" ? "#CBD5E0" : "#4A5568",
                        "&:hover": {
                          borderColor: colorMode === "light" ? "#A0AEC0" : "#718096",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: colorMode === "light" ? "black" : "gray.700",
                      }),
                      multiValue: (base) => ({
                        ...base,
                        color: "#fff",
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: colorMode === "light" ? "#717274" : "white",
                        boxShadow: "0px 10px 20px rgba(0,0,0,0.2)",
                        border: "1px solid",
                        borderColor: colorMode === "light" ? "#717274" : "#718096",
                        borderRadius: "4px",
                        zIndex: 9999,
                      }),
                      option: (base) => ({
                        ...base,
                        color: colorMode === "light" ? "white" : "black",
                        backgroundColor: colorMode === "light" ? "#717274" : "white",
                      }),
                    }}
                    id={input.id}
                    value={input.options.filter((option) =>
                      settings[input.id] && Array.isArray(settings[input.id])
                        ? settings[input.id].includes(option.value)
                        : settings[input.id] === option.value
                    )}
                    onChange={(selectedOptions) =>
                      handleSelectChange(input.id, selectedOptions, input.isMulti)
                    }
                    options={input.options}
                    isMulti={input.isMulti}
                    isDisabled={viewOnly}
                  />
                ))}

              {/* Text */}
              {input.type === "text" && (
                <Flex align="center">
                  {input.icon && (
                    <Tooltip label={input.name}>
                      <FontAwesomeIcon icon={input.icon} style={{ marginRight: "8px" }} size="lg" />
                    </Tooltip>
                  )}
                  <Text id={input.id} placeholder={input.name} ml={4}>
                    {settings[input.id]}
                  </Text>
                </Flex>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default GenericSettings;
